import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Mail, ArrowLeft, Plus, Search, Filter, Send, 
  Calendar, Users, Copy, Archive, BarChart, FileText 
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const EmailBroadcasts = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [activeTab, setActiveTab] = useState("campaigns");
  const [searchQuery, setSearchQuery] = useState("");

  // Mock data
  const campaigns = [
    {
      id: 1,
      name: "Summer Registration Reminder",
      status: "Sent",
      sentDate: "Jun 15, 2023",
      recipients: 356,
      openRate: "42%",
      clickRate: "18%",
    },
    {
      id: 2,
      name: "Fall Competition Announcement",
      status: "Draft",
      lastEdited: "Jul 23, 2023",
      recipients: 412,
    },
    {
      id: 3,
      name: "Holiday Special Promotion",
      status: "Scheduled",
      scheduledDate: "Nov 15, 2023",
      recipients: 520,
    },
    {
      id: 4,
      name: "Important Studio Updates",
      status: "Sent",
      sentDate: "May 02, 2023",
      recipients: 378,
      openRate: "38%",
      clickRate: "12%",
    },
  ];

  const tabs = [
    { id: "campaigns", label: "Campaigns", icon: Mail },
    { id: "templates", label: "Templates", icon: FileText },
    { id: "analytics", label: "Analytics", icon: BarChart },
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/admin/marketing")}
            className="btn btn-circle btn-ghost"
            aria-label="Back to Marketing Hub"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail size={24} className="text-primary" />
              Email Broadcasts
            </h1>
            <p className="text-base-content/60">
              Create and send targeted email campaigns to your audience
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={() => navigate("/admin/marketing/email-broadcasts/new")}
            className="btn btn-primary"
          >
            <Plus size={16} />
            New Campaign
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs tabs-boxed bg-base-200 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab gap-2 ${activeTab === tab.id ? "tab-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main content area */}
      <div className={`${
        isDarkMode
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5 shadow-sm'
          : 'bg-base-100 shadow-sm'
      } rounded-box p-6`}>
        {activeTab === "campaigns" && (
          <div className="space-y-6">
            {/* Search and filter */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search size={16} className="text-base-content/40" />
                </div>
                <input 
                  type="text" 
                  className="input input-bordered w-full pl-10"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <button className="btn btn-outline btn-sm">
                  <Filter size={14} />
                  Filter
                </button>
                <select className="select select-bordered select-sm">
                  <option value="all">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="sent">Sent</option>
                </select>
              </div>
            </div>

            {/* Campaigns list */}
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Campaign Name</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Recipients</th>
                    <th>Performance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-base-200 cursor-pointer">
                      <td className="font-medium" onClick={() => navigate(`/admin/marketing/email-broadcasts/${campaign.id}`)}>
                        {campaign.name}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span 
                            className={`w-2 h-2 rounded-full ${
                              campaign.status === "Sent" 
                                ? "bg-success" 
                                : campaign.status === "Draft" 
                                ? "bg-secondary" 
                                : "bg-warning"
                            }`} 
                          />
                          {campaign.status}
                        </div>
                      </td>
                      <td>
                        {campaign.status === "Sent" && (
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            {campaign.sentDate}
                          </div>
                        )}
                        {campaign.status === "Draft" && (
                          <div className="text-base-content/60">
                            Last edited: {campaign.lastEdited}
                          </div>
                        )}
                        {campaign.status === "Scheduled" && (
                          <div className="flex items-center gap-1 text-warning">
                            <Calendar size={14} />
                            {campaign.scheduledDate}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Users size={14} />
                          {campaign.recipients}
                        </div>
                      </td>
                      <td>
                        {campaign.status === "Sent" ? (
                          <div className="flex flex-col">
                            <span>Open: {campaign.openRate}</span>
                            <span>Click: {campaign.clickRate}</span>
                          </div>
                        ) : (
                          <span className="text-base-content/60">—</span>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button className="btn btn-ghost btn-xs" title="Edit">
                            <FileText size={14} />
                          </button>
                          <button className="btn btn-ghost btn-xs" title="Duplicate">
                            <Copy size={14} />
                          </button>
                          {campaign.status === "Draft" && (
                            <button className="btn btn-ghost btn-xs text-success" title="Send">
                              <Send size={14} />
                            </button>
                          )}
                          <button className="btn btn-ghost btn-xs" title="Archive">
                            <Archive size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-base-content/60">
                Showing 1-4 of 4 campaigns
              </span>
              <div className="join">
                <button className="join-item btn btn-sm btn-outline" disabled>
                  Previous
                </button>
                <button className="join-item btn btn-sm btn-outline">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "templates" && (
          <div className="flex flex-col items-center justify-center py-12">
            <Mail size={48} className="text-base-content/20 mb-4" />
            <h3 className="text-lg font-medium">Email Templates</h3>
            <p className="text-base-content/60 mb-6">
              Create reusable email templates for your campaigns
            </p>
            <button className="btn btn-primary">
              <Plus size={16} />
              Create Template
            </button>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="flex flex-col items-center justify-center py-12">
            <BarChart size={48} className="text-base-content/20 mb-4" />
            <h3 className="text-lg font-medium">Email Analytics</h3>
            <p className="text-base-content/60 mb-6">
              View performance metrics for your email campaigns
            </p>
            <button className="btn btn-outline">
              View Reports
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailBroadcasts; 